// ===========================================
// Permission Constants & Types (RBAC) - Frontend
// ===========================================
// This file is intentionally duplicated from apps/backend/src/types/permissions.types.ts
// to avoid cross-workspace imports in the monorepo. Keep both files in sync when
// adding or renaming resources/actions.

// ===========================================
// PERMISSION ACTIONS
// ===========================================

export const PERMISSION_ACTIONS = ['read', 'write', 'delete', 'export'] as const;
export type PermissionAction = (typeof PERMISSION_ACTIONS)[number];

// ===========================================
// PERMISSION RESOURCES
// ===========================================

export const PERMISSION_RESOURCES = {
  dashboard: 'dashboard',
  messages: 'messages',
  meet_and_greet: 'meet_and_greet',
  enquiries: 'enquiries',
  events: 'events',
  school_setup: 'school_setup',
  class_config: 'class_config',
  gamification: 'gamification',
  people_teachers: 'people_teachers',
  people_families: 'people_families',
  people_students: 'people_students',
  student_onboarding: 'student_onboarding',
  scheduling: 'scheduling',
  operations_invoices: 'operations_invoices',
  operations_subscriptions: 'operations_subscriptions',
  operations_pricing: 'operations_pricing',
  operations_payments: 'operations_payments',
  operations_refunds: 'operations_refunds',
  registration_fees: 'registration_fees',
  reports: 'reports',
  communications_newsletters: 'communications_newsletters',
  communications_emails: 'communications_emails',
  communications_settings: 'communications_settings',
  integrations: 'integrations',
  import_export: 'import_export',
  security_audit: 'security_audit',
  system_settings: 'system_settings',
} as const;

export type PermissionResource = (typeof PERMISSION_RESOURCES)[keyof typeof PERMISSION_RESOURCES];

// ===========================================
// RESOURCE → VALID ACTIONS MAP
// ===========================================

export const RESOURCE_ACTION_MAP: Record<PermissionResource, readonly PermissionAction[]> = {
  dashboard: ['read'],
  messages: ['read', 'write'],
  meet_and_greet: ['read', 'write', 'delete'],
  // `delete` LEFT in roles slice 2: deleting an enquiry is the owner's (ownerOnly, roles plan R6).
  enquiries: ['read', 'write'],
  events: ['read', 'write', 'delete'],
  school_setup: ['read', 'write', 'delete'],
  class_config: ['read', 'write', 'delete'],
  gamification: ['read', 'write', 'delete'],
  people_teachers: ['read', 'write', 'delete'],
  people_families: ['read', 'write', 'delete'],
  // `delete` LEFT in roles slice 2 (plan V18): no route or control read it after slice 1a
  // (deactivating a student is `.write`). A future student hard delete brings it back with its route.
  people_students: ['read', 'write'],
  // The onboarding check-in (enquiries Phase 6): `read` sees the page, `write` ticks an item.
  student_onboarding: ['read', 'write'],
  scheduling: ['read', 'write', 'delete'],
  operations_invoices: ['read', 'write', 'delete'],
  operations_subscriptions: ['read', 'write', 'delete'],
  operations_pricing: ['read', 'write', 'delete'],
  // `write` LEFT in roles slice 2: it gated nothing (Stripe Connect onboarding is ownerOnly).
  operations_payments: ['read'],
  // #642: refunds are the school's decision (Andrew 29/10/2026); withheld from the Staff role.
  operations_refunds: ['write'],
  // #374: read = the reports + the fee state; write = Assign to child, the manual credit, Not proceeding.
  registration_fees: ['read', 'write'],
  // `write` added 2026-09-16 for the Report settings page. Keep in step with
  // apps/backend/src/types/permissions.types.ts — these two files are duplicated
  // deliberately (no cross-workspace imports) and are pinned by
  // tests/unit/types/permissions.parity.test.ts.
  reports: ['read', 'write', 'export'],
  communications_newsletters: ['read', 'write', 'delete'],
  communications_emails: ['read', 'write', 'delete'],
  communications_settings: ['read', 'write'],
  integrations: ['read', 'write'],
  import_export: ['read', 'write'],
  security_audit: ['read'],
  system_settings: ['read', 'write'],
  // `roles_management` LEFT the catalogue in roles slice 1a: roles and staff assignment are
  // the OWNER's (ownerOnly, roles plan R1/R6). A stored grant for it is dropped on read
  // by `flattenPermissions`, and slice 2's migration (`harden_role_grants`) deleted the stored rows.
};

// ===========================================
// PERMISSION STRING TYPE
// ===========================================

export type PermissionString = `${PermissionResource}.${PermissionAction}`;

// ===========================================
// PERMISSION GROUPS (for two-tier UI)
// ===========================================

export interface PermissionGroupDefinition {
  key: string;
  label: string;
  description: string;
  resources: {
    key: PermissionResource;
    label: string;
    actions: readonly PermissionAction[];
  }[];
}

export const PERMISSION_GROUPS: PermissionGroupDefinition[] = [
  {
    key: 'general',
    label: 'General',
    description: 'Dashboard and messaging',
    resources: [
      { key: 'dashboard', label: 'Dashboard', actions: RESOURCE_ACTION_MAP.dashboard },
      { key: 'messages', label: 'Messages', actions: RESOURCE_ACTION_MAP.messages },
    ],
  },
  {
    key: 'crm',
    label: 'CRM',
    description: 'Meet & Greet bookings, enquiries, and events',
    resources: [
      { key: 'meet_and_greet', label: 'Meet & Greet', actions: RESOURCE_ACTION_MAP.meet_and_greet },
      { key: 'enquiries', label: 'Enquiries', actions: RESOURCE_ACTION_MAP.enquiries },
      { key: 'events', label: 'Events', actions: RESOURCE_ACTION_MAP.events },
    ],
  },
  {
    key: 'school_setup',
    label: 'School Setup',
    description: 'Terms, locations, and rooms',
    resources: [
      { key: 'school_setup', label: 'Terms, Locations & Rooms', actions: RESOURCE_ACTION_MAP.school_setup },
      { key: 'class_config', label: 'Class Configuration', actions: RESOURCE_ACTION_MAP.class_config },
    ],
  },
  {
    key: 'people',
    label: 'People',
    description: 'Teachers, families, students, and onboarding',
    resources: [
      { key: 'people_teachers', label: 'Teachers', actions: RESOURCE_ACTION_MAP.people_teachers },
      { key: 'people_families', label: 'Families', actions: RESOURCE_ACTION_MAP.people_families },
      { key: 'people_students', label: 'Students', actions: RESOURCE_ACTION_MAP.people_students },
      { key: 'student_onboarding', label: 'Onboarding check-in', actions: RESOURCE_ACTION_MAP.student_onboarding },
    ],
  },
  {
    key: 'scheduling',
    label: 'Scheduling',
    description: 'Classes, calendar, and extra lessons',
    resources: [
      { key: 'scheduling', label: 'Classes, Calendar & Extra Lessons', actions: RESOURCE_ACTION_MAP.scheduling },
    ],
  },
  {
    key: 'gamification',
    label: 'Gamification',
    description: 'Achievements, progress, and leaderboards',
    resources: [
      { key: 'gamification', label: 'Achievements & Progress', actions: RESOURCE_ACTION_MAP.gamification },
    ],
  },
  {
    key: 'operations',
    label: 'Operations',
    description: 'Invoices, subscriptions, pricing, payments and refunds',
    resources: [
      { key: 'operations_invoices', label: 'Invoices', actions: RESOURCE_ACTION_MAP.operations_invoices },
      { key: 'operations_subscriptions', label: 'Subscriptions', actions: RESOURCE_ACTION_MAP.operations_subscriptions },
      { key: 'operations_pricing', label: 'Pricing & Products', actions: RESOURCE_ACTION_MAP.operations_pricing },
      { key: 'operations_payments', label: 'Payment Settings', actions: RESOURCE_ACTION_MAP.operations_payments },
      { key: 'operations_refunds', label: 'Refunds', actions: RESOURCE_ACTION_MAP.operations_refunds },
      { key: 'registration_fees', label: 'Registration fees', actions: RESOURCE_ACTION_MAP.registration_fees },
    ],
  },
  {
    key: 'reports',
    label: 'Reports',
    description: 'All reporting and analytics',
    resources: [
      { key: 'reports', label: 'Reports & Analytics', actions: RESOURCE_ACTION_MAP.reports },
    ],
  },
  {
    key: 'communications',
    label: 'Communications',
    description: 'Newsletters, emails, and settings',
    resources: [
      { key: 'communications_newsletters', label: 'Newsletters', actions: RESOURCE_ACTION_MAP.communications_newsletters },
      { key: 'communications_emails', label: 'Custom, System & Sequence Emails', actions: RESOURCE_ACTION_MAP.communications_emails },
      { key: 'communications_settings', label: 'Email Branding & Settings', actions: RESOURCE_ACTION_MAP.communications_settings },
    ],
  },
  // The `website` group (pages, blocks, blog, settings) was removed 2026-09-16
  // with the website builder (md/plan/website-removal.md). Stale `website.*`
  // grants on existing custom roles are skipped by the backend.
  {
    key: 'system',
    label: 'System',
    description: 'Integrations, import/export, security, and settings',
    resources: [
      { key: 'integrations', label: 'Integrations', actions: RESOURCE_ACTION_MAP.integrations },
      { key: 'import_export', label: 'Import / Export', actions: RESOURCE_ACTION_MAP.import_export },
      { key: 'security_audit', label: 'Security Audit Log', actions: RESOURCE_ACTION_MAP.security_audit },
      { key: 'system_settings', label: 'System Settings', actions: RESOURCE_ACTION_MAP.system_settings },
    ],
  },
];

// ===========================================
// VALIDATION HELPERS
// ===========================================

/**
 * Check if a resource + action combination is valid
 */
export function isValidPermission(resource: string, action: string): boolean {
  const validActions = RESOURCE_ACTION_MAP[resource as PermissionResource];
  if (!validActions) return false;
  return validActions.includes(action as PermissionAction);
}
